import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import './CompanySelector.css';

/**
 * Company selector — sits in the top header, to the LEFT of the station selector.
 *
 * It only renders for users who can operate in MORE THAN ONE company (their own
 * plus at least one accepted invitation). Single-company users — the vast
 * majority — never see it, and the app behaves exactly as before.
 *
 * Picking a company stores `pg_company_id` (so api.js sends `X-Company-Id`),
 * pins that company's base station, and reloads so every module refetches in
 * the new company's scope. Choosing "my own company" clears the header.
 */
export default function CompanySelector() {
  const { t } = useTranslation();
  const { user, currentCompanyId, setCurrentCompany } = useAuth();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setL] = useState(false);
  const ref = useRef(null);

  /* ── Load the companies I can operate in ── */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setL(true);
    api.companies.mine()
      .then(rows => { if (!cancelled) setList(Array.isArray(rows) ? rows : []); })
      .catch(()   => { if (!cancelled) setList([]); })
      .finally(() => { if (!cancelled) setL(false); });
    return () => { cancelled = true; };
  }, [user]);

  /* ── Close on outside click ── */
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!user) return null;
  // Sub-users belong to a single company — no switcher for them.
  if (user.actor_type === 'user') return null;
  // Backward-compat: only surface the control when there's a real choice.
  if (list.length <= 1) return null;

  const homeCompany = list.find(c => c.is_home) || list[0];
  const activeId    = currentCompanyId || homeCompany?.client_id;
  const current     = list.find(c => c.client_id === activeId) || homeCompany;

  const pick = (c) => {
    setOpen(false);
    if (c.client_id === activeId) return;
    setCurrentCompany(c.client_id, c.base_station_id, !!c.is_home);
    // Full reload so all 30+ modules refetch cleanly in the new company scope.
    setTimeout(() => window.location.reload(), 50);
  };

  return (
    <div className="company-selector" ref={ref}>
      <button className="company-selector-button" onClick={() => setOpen(!open)} type="button">
        <span className="cs-icon">🏢</span>
        <span className="cs-name" title={current?.name}>
          {loading ? t('common.loading') : (current?.name || t('companySelector.company'))}
        </span>
        {current && !current.is_home && <span className="cs-badge cs-guest">{t('companySelector.guest')}</span>}
        <span className="cs-chev">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="company-selector-menu">
          <div className="cs-section-label">{t('companySelector.switchCompany')}</div>
          {list.map(c => (
            <button
              key={c.client_id}
              className={`cs-item ${c.client_id === activeId ? 'cs-active' : ''}`}
              onClick={() => pick(c)}
              type="button"
            >
              <span className="cs-item-name">{c.name}</span>
              {c.is_home
                ? <span className="cs-badge cs-own">{t('companySelector.owner')}</span>
                : <span className="cs-role">{c.role_name}</span>}
              {c.client_id === activeId && <span className="cs-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
